import { CircleDashed, CircleCheck, Ruler, Users } from 'lucide-react';
import { Hours } from '@obraplan/core/domain/shared/hours';
import type {
  TeamFrontView,
  TeamOfDayView,
} from '@obraplan/core/application/contracts/team-measurement';
import { EmptyState } from '@/components/feedback/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TeamFrontMembers } from './TeamFrontMembers';
import { describeTeamMeasurement, formatTeamQuantity } from './team-measurement-format';
import { AvisoDaFila } from './AvisoDaFila';
import type { SyncDoCartao } from './fila-da-conferencia';

/**
 * A medição por equipe, no dia (Fase 30, etapa 1 — D1, RN-406).
 *
 * Na obra por equipe a quantidade não é de cada pessoa: é lançada **uma vez** por equipe ×
 * frente × serviço. Este painel é a lista do que a equipe tem para medir naquele dia — a
 * frente, o serviço, quem trabalhou nela e quanto cada um ficou, a meta, e se já foi medida.
 * As horas, a ausência e a hora extra seguem nos cartões de cada pessoa, ao lado.
 *
 * Componente de apresentação: quem decide o que acontece ao tocar em "Medir" é a página, que
 * abre o diálogo (`TeamMeasurementDialog`) e chama o caso de uso.
 */
export interface TeamMeasurementPanelProps {
  /** As equipes do dia com as frentes a medir (`DayTeamMeasurementsView.teams`). */
  teams: readonly TeamOfDayView[];
  /** Quem olha pode lançar medição (a permissão de conferir). Sem ela os botões ficam parados. */
  editable: boolean;
  /** O dia aceita conferência — falso em dia futuro. */
  canInspect: boolean;
  /** Uma gravação em andamento: os botões esperam. */
  isPending?: boolean;
  onMeasure: (team: TeamOfDayView, front: TeamFrontView) => void;
  /** A fila do aparelho por frente: "aguardando envio", ou o recusado com o motivo (RN-611). */
  syncOf?: (team: TeamOfDayView, front: TeamFrontView) => SyncDoCartao | undefined;
}

export function TeamMeasurementPanel({
  teams,
  editable,
  canInspect,
  isPending = false,
  onMeasure,
  syncOf,
}: TeamMeasurementPanelProps) {
  // Equipe sem frente para medir não tem o que mostrar: não ganha um bloco vazio.
  const comFrentes = teams.filter((team) => team.fronts.length > 0);

  if (comFrentes.length === 0) {
    return (
      <EmptyState
        title="Nenhuma equipe com frente para medir neste dia"
        description="A medição por equipe vem de quem está numa equipe e tem horas planejadas numa frente com serviço. Funcionário sem equipe e terceirizado seguem medindo no cartão."
        icon={<Users className="size-8" />}
      />
    );
  }

  const podeMedir = editable && canInspect && !isPending;

  return (
    <section aria-label="Medição por equipe" className="flex flex-col gap-3">
      {!canInspect ? (
        <p className="text-sm text-(--color-content-muted)">
          Dia futuro — a medição abre quando o dia chegar.
        </p>
      ) : (
        !editable && (
          <p className="text-sm text-(--color-content-muted)">
            Você pode ver as medições, mas não lançá-las.
          </p>
        )
      )}

      {/* `grid-cols-1` e `min-w-0`: sem eles a coluna implícita cresce com o nome mais comprido
          e empurra o cartão para fora da tela de 375 px. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {comFrentes.map((team) => (
          <Card key={team.teamId} className="min-w-0">
            <CardContent className="flex flex-col gap-3 p-4">
              <header className="flex flex-col gap-0.5">
                <h3 className="flex items-center gap-2 text-lg font-medium">
                  <Users
                    className="size-4 shrink-0 text-(--color-content-muted)"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 break-words">{team.teamName}</span>
                </h3>
                {team.leaderName !== undefined && (
                  <p className="text-sm text-(--color-content-muted)">
                    Encarregado: {team.leaderName}
                  </p>
                )}
              </header>

              <ul className="flex flex-col gap-2">
                {team.fronts.map((front) => (
                  <FrontItem
                    key={`${front.activityId}|${front.serviceId}`}
                    team={team}
                    front={front}
                    podeMedir={podeMedir}
                    onMeasure={onMeasure}
                    {...(() => {
                      const fila = syncOf?.(team, front);
                      return fila === undefined ? {} : { fila };
                    })()}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function FrontItem({
  team,
  front,
  podeMedir,
  onMeasure,
  fila,
}: {
  team: TeamOfDayView;
  front: TeamFrontView;
  podeMedir: boolean;
  onMeasure: (team: TeamOfDayView, front: TeamFrontView) => void;
  fila?: SyncDoCartao;
}) {
  const medida = front.measurement;
  const acao = medida === undefined ? 'Medir' : 'Corrigir medição';
  // Sem unidade não há número que alguém saiba ler depois (RN-232): a frente espera o cadastro.
  const semUnidade = front.unit === undefined;

  return (
    <li className="flex flex-col gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words text-sm font-medium">{front.activityLabel}</p>
          <p className="text-xs text-(--color-content-muted)">
            {front.towerName} · {front.floorName}
          </p>
          <p className="break-words text-sm">{front.serviceName}</p>
        </div>
        <span
          className="tabular shrink-0 font-medium"
          title="Horas somadas de quem trabalhou nesta frente"
        >
          {Hours.ofQuarters(front.quarterHours).format()}
        </span>
      </div>

      <TeamFrontMembers members={front.members} />

      {fila !== undefined && (
        <AvisoDaFila
          sync={fila}
          pendente="Aguardando envio"
          recusado="O servidor não aceitou a medição feita sem sinal"
        />
      )}

      <p className="text-xs text-(--color-content-muted)">
        {front.plannedQuantity === undefined
          ? 'Sem meta lançada'
          : `Meta ${formatTeamQuantity(front.plannedQuantity, front.unit)}`}
      </p>

      {medida === undefined ? (
        <p className="flex items-start gap-1.5 text-sm text-(--color-state-warn)">
          <CircleDashed className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Falta medir
        </p>
      ) : (
        <p className="flex items-start gap-1.5 break-words text-sm text-(--color-state-ok)">
          <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {describeTeamMeasurement(medida, front.unit)}
        </p>
      )}

      {semUnidade && (
        <p className="text-xs text-(--color-content-muted)">
          O serviço não tem unidade — cadastre em Configurações → Serviços para medir.
        </p>
      )}

      <Button
        variant={medida === undefined ? 'primary' : 'secondary'}
        className="min-h-11 w-full sm:w-auto sm:self-start md:min-h-9"
        disabled={!podeMedir || semUnidade}
        aria-label={`${acao} — ${front.activityLabel} · ${front.serviceName} (${team.teamName})`}
        onClick={() => onMeasure(team, front)}
      >
        <Ruler aria-hidden="true" />
        {acao}
      </Button>
    </li>
  );
}
